import { useEffect, useRef, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { Header } from 'common/ui/Header';
import { AnalyseClearRequirementsButton } from 'modules/analyse/components/AnalyseClearRequirementsButton';
import { NoATSJobDescriptionStep } from 'pages/CandidatesRanking/components/steps/NoATSJobDescriptionStep';
import { NoATSUploadCVStep } from 'pages/CandidatesRanking/components/steps/NoATSUploadCVStep';
import { GenerateRequirementsStep } from 'pages/CandidatesRanking/components/steps/GenerateRequirementsStep';
import { AnalysisRequirementInput } from 'modules/analyse/types/AnalysisRequirementInput';
import { RequirementsSetupStep } from 'pages/CandidatesRanking/components/steps/RequirementsSetupStep';
import { RulesetFormHandle } from 'modules/analyse/components/RulesetForm';
import { AutomationTaskInput } from 'generated-graphql/types';
import { NoATSRequirementsSummaryStep } from 'pages/CandidatesRanking/components/steps/NoATSRequirementsSummaryStep';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';

const container: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const bodyContainer: SxProps = {
  mt: 4
};

enum NoATSStepName {
  JOB_DESCRIPTION_INPUT,
  UPLOAD_CV,
  GENERATE_REQUIREMENTS,
  REQUIREMENTS_SETUP,
  REQUIREMENTS_SUMMARY
}

type Step = {
  name: NoATSStepName;
  title: string;
  subtitle: string;
  component: React.ReactNode;
  handleBack: () => void;
};

export type FileWithS3Key = {
  file: File;
  s3Key: string;
};

export const NoATSAnalyseCreation = () => {
  const navigate = useNavigate();
  const organizationConfig = useOrganizationConfig();

  const [stepName, setStepName] = useState(NoATSStepName.JOB_DESCRIPTION_INPUT);
  const [jobDescription, setJobDescription] = useState('');
  const [externalId, setExternalId] = useState<string | undefined>();
  const [filesWithS3Keys, setFilesWithS3Keys] = useState<FileWithS3Key[]>([]);
  const [hasGeneratedRequirements, setHasGeneratedRequirements] =
    useState(false);
  const [groupName, setGroupName] = useState<string>();
  const [requirements, setRequirements] = useState<AnalysisRequirementInput[]>(
    []
  );
  const [automationTasks, setAutomationTasks] = useState<AutomationTaskInput[]>(
    []
  );

  const [autofillDefaultTemplate, setAutofillDefaultTemplate] = useState(true);
  const [templateId, setTemplateId] = useState<string | undefined>();
  const rulesetFormRef = useRef<RulesetFormHandle>(null);

  useEffect(() => {
    if (!templateId && organizationConfig?.defaultAutomationTaskTemplateId) {
      setTemplateId(organizationConfig.defaultAutomationTaskTemplateId);
    }
  }, [organizationConfig]);

  const noATSSteps: Step[] = [
    {
      name: NoATSStepName.JOB_DESCRIPTION_INPUT,
      title: 'Choose jobs',
      subtitle: 'Select all the jobs to use for this analysis',
      component: (
        <NoATSJobDescriptionStep
          jobDescription={jobDescription}
          externalId={externalId}
          onNext={(jobDescription, externalId) => {
            setJobDescription(jobDescription);
            setExternalId(externalId);
            setStepName(NoATSStepName.UPLOAD_CV);
          }}
        />
      ),
      handleBack: () => {
        navigate(-1);
      }
    },
    {
      name: NoATSStepName.UPLOAD_CV,
      title: 'Upload CV',
      subtitle:
        'Upload multiple CVs as PDF, TXT, DOCX, or HTML files, so we can extract the data needed to build a campaign',
      component: (
        <NoATSUploadCVStep
          filesWithS3Keys={filesWithS3Keys}
          onNext={(filesWithS3Keys) => {
            setFilesWithS3Keys(filesWithS3Keys);
            const nextStep = hasGeneratedRequirements
              ? NoATSStepName.REQUIREMENTS_SETUP
              : NoATSStepName.GENERATE_REQUIREMENTS;
            setStepName(nextStep);
          }}
          onSkip={() => {
            const nextStep = hasGeneratedRequirements
              ? NoATSStepName.REQUIREMENTS_SETUP
              : NoATSStepName.GENERATE_REQUIREMENTS;
            setStepName(nextStep);
          }}
        />
      ),
      handleBack: () => {
        setStepName(NoATSStepName.JOB_DESCRIPTION_INPUT);
      }
    },
    {
      name: NoATSStepName.GENERATE_REQUIREMENTS,
      title: 'Generate requirements',
      subtitle: '',
      component: (
        <GenerateRequirementsStep
          jobDescription={jobDescription}
          onGenerateRequirements={(requirements) => {
            setHasGeneratedRequirements(true);
            setRequirements(requirements);
            setStepName(NoATSStepName.REQUIREMENTS_SETUP);
          }}
        />
      ),
      handleBack: () => {
        setStepName(NoATSStepName.UPLOAD_CV);
      }
    },
    {
      name: NoATSStepName.REQUIREMENTS_SETUP,
      title: 'Analysis set up',
      subtitle: '',
      component: (
        <RequirementsSetupStep
          formRef={rulesetFormRef}
          requirements={requirements}
          automationTasks={automationTasks}
          templateId={templateId}
          autofillDefaultTemplate={autofillDefaultTemplate}
          onNext={({ groupName, requirements, automationTasks }) => {
            setAutofillDefaultTemplate(false);
            setGroupName(groupName);
            setRequirements(requirements);
            setAutomationTasks(automationTasks);
            setStepName(NoATSStepName.REQUIREMENTS_SUMMARY);
          }}
          onTemplateChange={(templateId) => {
            setTemplateId(templateId);
          }}
        />
      ),
      handleBack: () => {
        setStepName(NoATSStepName.UPLOAD_CV);
      }
    },
    {
      name: NoATSStepName.REQUIREMENTS_SUMMARY,
      title: 'Analysis summary',
      subtitle: '',
      component: (
        <NoATSRequirementsSummaryStep
          filesWithS3Keys={filesWithS3Keys}
          jobDescription={jobDescription}
          externalId={externalId}
          candidateBatchGroupName={groupName}
          requirements={requirements}
          automationTasks={automationTasks}
          onBack={() => {
            setStepName(NoATSStepName.REQUIREMENTS_SETUP);
          }}
        />
      ),
      handleBack: () => {
        setStepName(NoATSStepName.REQUIREMENTS_SETUP);
      }
    }
  ];

  const currentStep = noATSSteps.find((step) => step.name === stepName);

  if (!currentStep) {
    return <div>Step not found</div>;
  }

  return (
    <Box sx={container}>
      {currentStep.name !== NoATSStepName.GENERATE_REQUIREMENTS && (
        <Header
          title={currentStep.title}
          items={[currentStep.subtitle]}
          breadcrumbs={[{ name: 'Analyse', path: Paths.ANALYSE }]}
          currentBreadcrumb={currentStep.title}
          onBack={currentStep.handleBack}
          actions={
            currentStep.name === NoATSStepName.REQUIREMENTS_SETUP ? (
              <AnalyseClearRequirementsButton
                onClearRequirements={() =>
                  rulesetFormRef.current?.clearRequirements()
                }
              />
            ) : undefined
          }
        />
      )}
      <Box sx={bodyContainer}>{currentStep.component}</Box>
    </Box>
  );
};
